/**
 * The service page's standard look (the prototype's Option 1): the breadcrumb,
 * then the photos, name, short line, facts, description, preparation note and
 * the location and cancellation lines on the left, the booking column on the
 * right. The photos and booking column are their own pieces, passed in. Draws
 * the behavior half's state; holds no rule. Each line drops out when unset.
 */
import type { ReactNode } from 'react';

import { LOCATION_KIND_LABEL } from '@telify/shared';

import { partsOf, pieceRoot } from '../../contract/runtime';
import { formatDuration, formatLocation, priceText } from '../../format';
import shared from '../../styles/shared.module.css';
import { MoveLink } from '../../ui/MoveLink';
import type { ServicePageRuntime } from './useServicePage';
import css from './ServicePage.module.css';
import { contract } from './contract';

const part = partsOf(contract);

/** The studio's text as paragraphs, split on a blank line. */
export function Paragraphs({ text }: { text: string }) {
  return (
    <>
      {text
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p, i) => (
          <p key={i}>{p}</p>
        ))}
    </>
  );
}

export interface ServicePageSlots {
  /** The photo gallery piece. */
  readonly photos?: ReactNode;
  /** The booking column piece. */
  readonly booking?: ReactNode;
  /** Message the studio, under the details when her site turns it on (web-studio-sites D15). */
  readonly message?: ReactNode;
}

/**
 * The breadcrumb's leading crumbs: the studio's home; on a custom site her home and her booking page by the names
 * her site gives them (web-studio-sites D13). Shared with the event page.
 */
export function HomeCrumbs({ state, between }: { state: Pick<ServicePageRuntime['state'], 'studioName' | 'siteLinks'>; between?: ReactNode }) {
  const site = state.siteLinks;
  const home = site?.home.label ?? state.studioName;
  return (
    <>
      {home && (
        <>
          <MoveLink to={site?.home.path ?? '/'} {...part('crumb-home')}>
            {home}
          </MoveLink>
          <span className={css.sep} aria-hidden="true">
            /
          </span>
        </>
      )}
      {site ? (
        <>
          <MoveLink to={site.book.path} {...part('crumb-book')}>
            {site.book.label}
          </MoveLink>
          <span className={css.sep} aria-hidden="true">
            /
          </span>
        </>
      ) : (
        between
      )}
    </>
  );
}

export function ServicePageView({ state, options, photos, booking, message }: ServicePageRuntime & ServicePageSlots) {
  const s = state.service;
  if (state.status === 'error') {
    return (
      <div className={`${shared.wrap} ${css.page}`} {...pieceRoot(contract)} {...part('page')}>
        <p className={css.error} {...part('error')}>
          This page could not load. Please try again in a moment.
        </p>
      </div>
    );
  }
  if (!s) return <div className={css.page} aria-busy="true" {...pieceRoot(contract)} {...part('page')} />;

  const where = formatLocation(s.location);
  return (
    <div className={css.page} {...pieceRoot(contract)} {...part('page')}>
      <div className={shared.wrap}>
        <nav className={css.crumbs} aria-label="Breadcrumb" {...part('crumbs')}>
          <HomeCrumbs state={state} />
          <span className={css.here} aria-current="page" {...part('crumb-here')}>
            {s.name}
          </span>
        </nav>

        <div className={css.grid}>
          <article>
            <div className={css.photos} {...part('photos')}>
              {photos}
            </div>
            <div className={css.title}>
              <h1 className={css.h1} {...part('name')}>
                {s.name}
              </h1>
              {s.summary && (
                <p className={css.lead} {...part('summary')}>
                  {s.summary}
                </p>
              )}
            </div>
            <div className={css.facts} {...part('facts')}>
              <div className={css.fact} {...part('duration')}>
                <div className={css.factK}>Duration</div>
                <div className={css.factV}>{formatDuration(s.durationMinutes)}</div>
              </div>
              <div className={css.fact} {...part('format')}>
                <div className={css.factK}>Format</div>
                <div className={css.factV}>{LOCATION_KIND_LABEL[s.modality]}</div>
              </div>
              <div className={css.fact} {...part('price')}>
                <div className={css.factK}>Price</div>
                <div className={`${css.factV} ${css.accent}`}>{priceText(s.price)}</div>
              </div>
            </div>

            {s.description && (
              <section className={css.sec}>
                {options.aboutHeading && (
                  <h2 className={css.h2} {...part('about-heading')}>
                    {options.aboutHeading}
                  </h2>
                )}
                <div className={css.body} {...part('description')}>
                  <Paragraphs text={s.description} />
                </div>
              </section>
            )}

            {s.preparation && (
              <section className={css.sec} {...part('preparation')}>
                <h2 className={css.h2} {...part('preparation-heading')}>
                  {s.preparation.heading}
                </h2>
                <div className={css.body} {...part('preparation-text')}>
                  <Paragraphs text={s.preparation.text} />
                </div>
              </section>
            )}

            <dl className={css.dl} {...part('details')}>
              <div {...part('location')}>
                <dt>Location</dt>
                <dd>
                  {where.t1}
                  {where.t2 && <small>{where.t2}</small>}
                </dd>
              </div>
              {s.cancellationText.trim() && (
                <div {...part('cancellation')}>
                  <dt>Cancellation</dt>
                  <dd>{s.cancellationText}</dd>
                </div>
              )}
            </dl>
            {message && <div className={css.message}>{message}</div>}
          </article>
          <div className={css.side} {...part('booking')}>
            {booking}
          </div>
        </div>
      </div>
    </div>
  );
}
